import React, { useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useJobStats } from '@/hooks/useJobs';
import { useMonitoringNavigation } from '@/hooks/useMonitoringNavigation';
import { CustomerData } from '@/sections/customerManagement/types';
import {
  AssetMonitoringSection,
  IntegrationMonitoringSection,
  JobMonitoringSection,
  SeedMonitoringSection,
  VulnerabilityMonitoringSection,
} from '@/sections/monitoring';
import { useMergeStatus } from '@/utils/api';
import {
  transformAssetMonitoring,
  transformSeedStats,
  transformVulnerabilityStats,
} from '@/utils/monitoring';

interface CustomerMonitoringTabProps {
  customer: CustomerData;
}

/**
 * CustomerMonitoringTab - Monitoring overview for a specific customer
 *
 * Displays comprehensive monitoring metrics including:
 * - Vulnerability counts by severity (open/pending)
 * - Asset counts by category (cloud, network, code, etc.)
 * - Seed counts by type (domains, IPs, CIDRs)
 * - Integration status
 * - Job execution statistics
 *
 * All sections support navigation to detailed views with customer impersonation.
 *
 * @remarks
 * This component uses React.memo to prevent unnecessary re-renders.
 * Data is cached for 30 seconds and refetched every 30 seconds while visible.
 */
export const CustomerMonitoringTab: React.FC<CustomerMonitoringTabProps> =
  React.memo(
    ({ customer }) => {
      const navigation = useMonitoringNavigation({
        customerEmail: customer.email,
      });

      /**
       * Handle navigation to assets page
       * @remarks Wrapped in useCallback to prevent child re-renders
       */
      const handleAssetsViewAll = React.useCallback(() => {
        navigation.navigateToAssets({});
      }, [navigation]);

      /**
       * Handle navigation to all jobs for this customer
       */
      const handleJobViewAll = React.useCallback(() => {
        navigation.navigateToJobs({});
      }, [navigation]);

      /**
       * Handle navigation to jobs filtered by status
       */
      const handleJobStatusClick = React.useCallback(
        (status: import('@/types').JobStatus) => {
          navigation.navigateToJobs({ status });
        },
        [navigation]
      );

      // Fetch all monitoring data
      const {
        data: monitoringData,
        status: monitoringStatus,
        error: monitoringError,
        refetch: refetchMonitoring,
      } = useGetMonitoringCounts({
        email: customer.email,
      });

      const {
        data: vulnStats,
        status: vulnStatus,
        error: vulnError,
        refetch: refetchVuln,
      } = useMy(
        {
          resource: 'statistic',
          query: '#risk#status',
        },
        {
          email: customer.email,
          staleTime: 0,
          gcTime: 0,
          refetchOnMount: 'always' as const,
        }
      );

      const {
        data: seedStats,
        status: seedStatus,
        error: seedError,
        refetch: refetchSeeds,
      } = useMy(
        {
          resource: 'statistic',
          query: '#seed#class#status#',
        },
        {
          email: customer.email,
          staleTime: 0,
          gcTime: 0,
          refetchOnMount: 'always' as const,
        }
      );

      const { jobStats, status: jobStatus } = useJobStats({
        email: customer.email,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always' as const,
      });

      // Merge all monitoring statuses so they load together
      const mergedMonitoringStatus = useMergeStatus(
        monitoringStatus,
        vulnStatus,
        seedStatus
      );
      const isMonitoringLoading = mergedMonitoringStatus === 'pending';

      // Transform API data to component format
      const vulnerabilityMetrics = useMemo(
        () => transformVulnerabilityStats(vulnStats),
        [vulnStats]
      );

      const assetSections = useMemo(
        () => transformAssetMonitoring(monitoringData),
        [monitoringData]
      );

      const seedMetrics = useMemo(
        () => transformSeedStats(seedStats),
        [seedStats]
      );

      // Error aggregation
      const errors = useMemo(
        () => ({
          monitoring: monitoringError,
          vulnerabilities: vulnError,
          seeds: seedError,
        }),
        [monitoringError, vulnError, seedError]
      );

      const hasErrors = Object.values(errors).some(err => err !== null);

      return (
        <div className="flex flex-col gap-4">
          {/* Error handling UI */}
          {hasErrors && (
            <div className="bg-chariot-error-bg border-chariot-error-border rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <div className="text-chariot-error-text flex-1">
                  <p className="font-semibold">
                    Failed to load some monitoring data
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {errors.monitoring && (
                      <li>Asset monitoring data failed to load</li>
                    )}
                    {errors.vulnerabilities && (
                      <li>Vulnerability data failed to load</li>
                    )}
                    {errors.seeds && <li>Seed data failed to load</li>}
                  </ul>
                </div>
                <button
                  onClick={() => {
                    if (errors.monitoring) refetchMonitoring();
                    if (errors.vulnerabilities) refetchVuln();
                    if (errors.seeds) refetchSeeds();
                  }}
                  className="text-chariot-error-text hover:text-chariot-error-dark rounded px-3 py-1.5 text-sm font-medium transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}
          {/* Vulnerability Monitoring */}
          <VulnerabilityMonitoringSection
            metrics={vulnerabilityMetrics}
            isLoading={isMonitoringLoading}
            onViewAll={navigation.navigateToVulnerabilities}
            onMetricClick={navigation.navigateToVulnerability}
          />

          {/* Asset Monitoring */}
          <AssetMonitoringSection
            sections={assetSections}
            isLoading={isMonitoringLoading}
            onViewAll={handleAssetsViewAll}
            onSectionClick={navigation.navigateToAssetSection}
            onCategoryClick={navigation.navigateToAssetCategory}
          />

          {/* Seed Monitoring */}
          <SeedMonitoringSection
            metrics={seedMetrics}
            isLoading={isMonitoringLoading}
            onViewAll={navigation.navigateToSeeds}
            onSeedClick={navigation.navigateToSeedType}
          />

          {/* Integration Monitoring */}
          <IntegrationMonitoringSection
            customerEmail={customer.email}
            onViewAll={navigation.navigateToIntegrations}
          />

          {/* Jobs Monitoring */}
          <JobMonitoringSection
            stats={jobStats}
            isLoading={jobStatus === 'pending'}
            onViewAll={handleJobViewAll}
            onStatusClick={handleJobStatusClick}
          />
        </div>
      );
    },
    (prevProps, nextProps) => {
      // Custom comparison - only re-render if customer email changes
      return prevProps.customer.email === nextProps.customer.email;
    }
  );

CustomerMonitoringTab.displayName = 'CustomerMonitoringTab';
